import { Injectable } from '@angular/core';

import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';

import { ApiClientService } from '@tablesready/api-client';
import { AppVariablesResponse } from '@shared/public/models';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { AppVariables, AppVariablesBrand } from './app-variables.interfaces';
import { Capacitor } from '@capacitor/core';

const DEFAULT_FULLCALENDAR_KEY = 'CC-Attribution-NonCommercial-NoDerivatives';

export const DEFAULT_APP_VERSION = '0.0.0';

const DEFAULT_VARS: AppVariables = {
  WHITELABEL_BRAND: AppVariablesBrand.TablesReady,
  WHITELABEL_LOGO: 'tr_logo_secondary_300_dark.png',
  WHITELABEL_HOSTNAME: 'https://host.tablesready.com',
  WHITELABEL_LINK: 'www.tablesready.com',
  WHITELABEL_SMS_CHARS: '320',
  WHITELABEL_SMS_NON_ANSI_CHARS: '140',
  ACCOUNT_DELETING_DELAY: '48 hours',
  FREE_PLAN_LOCATION_LIMIT: 2,
  MAX_SAVED_REPLIES: 10,
  FULL_CALENDAR_KEY: DEFAULT_FULLCALENDAR_KEY,
  UNPAID_PARTY_LIFETIME: 600,
  SERVICE_FEE: 2,
  APP_VERSION: DEFAULT_APP_VERSION,
  FIREBASE_API_KEY: '',
  FIREBASE_AUTH_DOMAIN: '',
  FIREBASE_PROJECT_ID: '',
  FIREBASE_STORAGE_BUCKET: '',
  FIREBASE_MESSAGING_SENDER_ID: '',
  FIREBASE_APP_ID: '',
  FIREBASE_VAPID_KEY: '',
  FIREBASE_SERVICE_WORKER_PATH: '/firebase-messaging-sw.js',
  CLOUDFLARE_TURNSTILE_SITE_KEY: '',
  CLOUDFLARE_TURNSTILE_MODE: 'off',
  CLOUDFLARE_TURNSTILE_ALLOW_UNAVAILABLE_BYPASS: false,
  SIGNUP_TURNSTILE_MODE: 'off',
};

@Injectable()
export class AppVariablesService  {
  readonly appVariables: Observable<AppVariables>;
  private _appVariables: AppVariables | null = null;

  constructor(
    private readonly http: ApiClientService,
    private readonly alertsService: AlertsService,
  ) {
    this.appVariables = this.getAppVariables().pipe(shareReplay(1));
  }

  getAppVariables(): Observable<AppVariables> {
    return this.http
      .get<AppVariablesResponse>('/appvariables')
      .pipe(
        map(({ success, data }): AppVariables => {
          if (success) {
            return {
              WHITELABEL_BRAND: data.WHITELABEL_BRAND as AppVariablesBrand,
              WHITELABEL_LINK: data.WHITELABEL_LINK,
              WHITELABEL_LOGO: data.WHITELABEL_LOGO,
              WHITELABEL_HOSTNAME: data.WHITELABEL_HOSTNAME,
              WHITELABEL_SMS_CHARS: data.WHITELABEL_SMS_CHARS,
              WHITELABEL_SMS_NON_ANSI_CHARS: data.WHITELABEL_SMS_NON_ANSI_CHARS,
              ACCOUNT_DELETING_DELAY: data.ACCOUNT_DELETING_DELAY,
              FREE_PLAN_LOCATION_LIMIT: data.FREE_PLAN_LOCATION_LIMIT,
              MAX_SAVED_REPLIES: data.MAX_SAVED_REPLIES,
              FULL_CALENDAR_KEY: data.FULL_CALENDAR_KEY,
              UNPAID_PARTY_LIFETIME: data.UNPAID_PARTY_LIFETIME,
              SERVICE_FEE: data.SERVICE_FEE,
              APP_VERSION: data.APP_VERSION,
              FIREBASE_API_KEY: data.FIREBASE_API_KEY,
              FIREBASE_AUTH_DOMAIN: data.FIREBASE_AUTH_DOMAIN,
              FIREBASE_PROJECT_ID: data.FIREBASE_PROJECT_ID,
              FIREBASE_STORAGE_BUCKET: data.FIREBASE_STORAGE_BUCKET,
              FIREBASE_MESSAGING_SENDER_ID: data.FIREBASE_MESSAGING_SENDER_ID,
              FIREBASE_APP_ID: data.FIREBASE_APP_ID,
              FIREBASE_VAPID_KEY: data.FIREBASE_VAPID_KEY,
              FIREBASE_SERVICE_WORKER_PATH: data.FIREBASE_SERVICE_WORKER_PATH,
              CLOUDFLARE_TURNSTILE_SITE_KEY: data.CLOUDFLARE_TURNSTILE_SITE_KEY,
              CLOUDFLARE_TURNSTILE_MODE: data.CLOUDFLARE_TURNSTILE_MODE || 'off',
              CLOUDFLARE_TURNSTILE_ALLOW_UNAVAILABLE_BYPASS:
                data.CLOUDFLARE_TURNSTILE_ALLOW_UNAVAILABLE_BYPASS === true,
              SIGNUP_TURNSTILE_MODE: data.SIGNUP_TURNSTILE_MODE || 'off',
            };
          }
          throw new Error();
        }),
        catchError(() => of(DEFAULT_VARS)),
        tap((appVariables) => {
          this._appVariables = appVariables;
          if (Capacitor.isNativePlatform()) {
            return;
          }

          const scriptTags = document.querySelectorAll('script');
          const versionPattern = /main\.(\d+\.\d+\.\d+)\.js/;
          let version = null;

          scriptTags.forEach((script) => {
            const match = script.src.match(versionPattern);
            if (match) {
              version = match[1];
            }
          });

          if (version && version !== appVariables.APP_VERSION && DEFAULT_APP_VERSION !== appVariables.APP_VERSION) {
            this.alertsService.add({
              id: 'app-out-of-date',
              type: IAlertTypes.warning,
              duration: 7 * 24 * 60 * 60 * 1000,
              time: Date.now(),
              heading: '',
              body: 'Your app version is out of date. Click here to reload the page link. If this doesn\'t work, please clear your browser cache.',
              actions: [
                {
                  label: 'Click here to reload.',
                  action: (): void => {
                    window.location.reload();
                  },
                },
              ],
            });
          }
        }),
      );
  }

  resolve() {
    return this.appVariables;
  }

  canActivateChild() {
    return this.appVariables.pipe(map(() => true));
  }

  canActivate() {
    return this.appVariables.pipe(map(() => true));
  }

  get() {
    return this._appVariables;
  }
}
